import { BookApiResponse, PublisherBookApiResponse } from 'api/api.types';
import {
  AppStoreButton,
  color,
  Flex,
  PrimaryHeading,
  PrimaryText,
  spacing,
  useColorMode,
  useIsMobileView,
  Heading,
  typography,
  Link,
  useDeviceDetect,
} from 'deepstash-ui';
import React from 'react';
import NextImage from 'src/components/image/NextImage';
import Footer from 'src/components/layout/components/Footer';
import FullWidthSectionWrapper from 'src/components/layout/components/FullWidthSectionWrapper';
import HeadMetadata from 'src/components/metadata/HeadMetadata';
import Navbar from 'src/components/navbar/Navbar';
import { GAP_SIZE_REM, slugify } from 'utils/global';
import { PageMetadata } from 'types';
import NextLink from 'src/components/navigation/NextLink';
import { useRouter } from 'next/router';

const BOOK_SIZE = { WIDTH: 180, HEIGHT: 260 };

const IMAGE_SIZE = 240;
const DEFAULT_AUTHOR_IMAGE =
  'https://static.deepstash.com/images/author-placeholder.png';

interface MasterBookAuthorPageProps {
  publisher: PublisherBookApiResponse;
  pageMetadata: PageMetadata;
}

const MasterBookAuthorPage: React.FC<MasterBookAuthorPageProps> = ({
  publisher,
  pageMetadata,
}) => {
  const { colorMode } = useColorMode();
  const isMobileView = useIsMobileView();
  const { isIos } = useDeviceDetect();
  const router = useRouter();

  const [descriptionExpanded, setDescriptionExpanded] = React.useState(false);

  const dividerLine = (
    <Flex height={2} bgColor={color[colorMode].underground} width={'100%'} />
  );

  const mobileAppSection = (
    <>
      <Flex flexDirection="column" mt="2rem">
        <PrimaryText
          type="semiBold"
          color={color[colorMode].textSecondary}
          size="l"
        >
          {`Read top ideas, quotes & summaries from ${publisher.name} books, with the Deepstash App.`}
        </PrimaryText>
        {isMobileView ? (
          <AppStoreButton w={'100%'} store={isIos ? 'Apple' : 'GooglePlay'} />
        ) : (
          <Flex
            flexDirection="row"
            justifyContent={'flex-start'}
            gridColumnGap={16}
          >
            <AppStoreButton
              w={spacing.toRem(192)}
              h={spacing.toRem(48)}
              store="Apple"
            />
            <AppStoreButton
              w={spacing.toRem(192)}
              h={spacing.toRem(48)}
              store="GooglePlay"
            />
          </Flex>
        )}
      </Flex>
    </>
  );

  const imageComponent = (
    <NextImage
      wrapperStyle={{
        width: IMAGE_SIZE,
        height: IMAGE_SIZE,
        borderRadius: IMAGE_SIZE,
        cursor: 'default',
      }}
      imageUrl={publisher.image ?? DEFAULT_AUTHOR_IMAGE}
      alt={publisher.name}
      blurhash={publisher.image_blurhash}
      priority={true}
    />
  );

  const descriptionSection = (
    <Flex flexDirection="column" mt={32}>
      <Flex flexDirection="row" justifyContent="space-between">
        <PrimaryText
          type="semiBold"
          color={color[colorMode].textSecondary}
          size="l"
        >
          {`About the author (not Wikipedia but close)`}
        </PrimaryText>
      </Flex>
      <PrimaryText
        size="m"
        color={color[colorMode].textSecondary}
        noOfLines={descriptionExpanded ? undefined : 3}
      >
        {publisher.description ??
          `${publisher.name} is an accomplished writer whose work has resonated with readers around the world. ${publisher.name} has published several books that explore a range of topics, from fiction to nonfiction, and has received critical acclaim for the ability to weave compelling narratives. ${publisher.name}'s writing style is known for its depth and authenticity, and has earned quite a loyal following of readers.`}
      </PrimaryText>
      <PrimaryText
        _hover={{ cursor: 'pointer' }}
        color={color[colorMode].text}
        onClick={() => {
          setDescriptionExpanded(prevValue => !prevValue);
        }}
      >
        {descriptionExpanded ? 'See Less' : 'Read full Bio'}
      </PrimaryText>
    </Flex>
  );

  const renderBookImageComponent = ({
    book,
    bookPageRoute,
  }: {
    book: BookApiResponse;
    bookPageRoute: string;
  }) => {
    return (
      <NextImage
        wrapperStyle={{
          width: BOOK_SIZE.WIDTH,
          height: BOOK_SIZE.HEIGHT,
        }}
        imageUrl={book.image}
        alt={`${publisher.name} Books`}
        blurhash={book.image_blurhash}
        priority={true}
        onClick={() => {
          router.push(bookPageRoute);
        }}
      />
    );
  };

  const renderBooksSection = () => {
    if (
      publisher.top_sources === undefined ||
      publisher.top_sources?.length === 0
    ) {
      return null;
    }
    return (
      <Flex flexDirection="column" mt={24}>
        <Heading
          color={color[colorMode].textSecondary}
          size={'h2'}
          {...typography.primaryHeadingFonts.small.semiBold}
        >
          {`${publisher.name} Books & Quotes`}
        </Heading>
        <Flex overflowX="scroll" flexWrap="nowrap" gridGap={32} mt={16} pb={32}>
          {publisher.top_sources.map((book, index) => {
            const bookSlug = slugify(book.title);
            const bookPageRoute = `/book/${book.id}/${bookSlug}`;

            return (
              <Flex
                flexDirection="column"
                gridRowGap={16}
                key={`book-${index}`}
              >
                {renderBookImageComponent({
                  book: book,
                  bookPageRoute: bookPageRoute,
                })}
                <NextLink href={bookPageRoute} passHref key={`book-${index}`}>
                  <Link _focus={undefined} _hover={undefined}>
                    <Heading
                      textAlign="center"
                      color={color[colorMode].text}
                      size={'h3'}
                      width={BOOK_SIZE.WIDTH}
                      overflowX="hidden"
                      {...typography.primaryTextFonts.medium.regular}
                    >
                      {`${book.title} Book Summary`}
                    </Heading>
                  </Link>
                </NextLink>
              </Flex>
            );
          })}
        </Flex>
      </Flex>
    );
  };

  return (
    <>
      <HeadMetadata pageMetadata={pageMetadata} />
      <FullWidthSectionWrapper>
        <Navbar />
      </FullWidthSectionWrapper>

      <FullWidthSectionWrapper
        py={GAP_SIZE_REM}
        containerProps={{
          flexDirection: isMobileView ? 'column' : 'row',
          gridGap: GAP_SIZE_REM,
        }}
      >
        {/** COL 1 */}
        <Flex
          justifyContent="flex-start"
          flexDirection="column"
          alignItems="center"
        >
          {imageComponent}
          <PrimaryText
            type="regular"
            color={color[colorMode].textSecondary}
            size="m"
            mt={spacing.M.rem}
          >
            {`Read & browse all books, key ideas and summaries from the book author ${publisher.name} in just 5 minutes.`}
          </PrimaryText>
        </Flex>

        {/** COL 2 */}
        <Flex overflowX="hidden" flexDirection="column">
          <PrimaryText
            type="semiBold"
            color={color[colorMode].textSecondary}
            size="l"
          >
            {`The best ${publisher.name} books, summaries, wiki, quotes & more`}
          </PrimaryText>
          <PrimaryHeading
            textSize="l"
            type="bold"
            size="h1"
            color={color[colorMode].text}
            mt={spacing.M.rem}
          >
            {publisher.name}
          </PrimaryHeading>
          {descriptionSection}
          {mobileAppSection}
          {dividerLine}
          {renderBooksSection()}
        </Flex>
      </FullWidthSectionWrapper>
      <Footer />
    </>
  );
};

export default MasterBookAuthorPage;
